/**
 * The Tower (+field, docs/design/zones.md): the stage after Mossvale, a tall silhouette on the
 * horizon of every Mossvale map, always in the same world direction (content puts a
 * `tower_landmark` prop hint far outside the map; only its direction is used).
 *
 * It is a backdrop like the sky dome: it follows the camera at a fixed distance, so it never gets
 * closer however far you walk ("on the horizon"). One merged, vertex-coloured mesh hazed toward
 * the sky, two slowly turning gold rings, warm window glints and a beacon at the top. Unlit and
 * shadowless: about 4 draws.
 */
import * as THREE from 'three';
import { combine, paint, xf } from './procs';
import { U } from '../style';
import { ATMOS } from './atmosphere/uniforms';

/** Backdrop distance (inside the 330 yd sky dome) and drawn height (≈ 16° above the horizon: the whole spire shows over the tree line). */
export const TOWER_DISTANCE = 280;
export const TOWER_HEIGHT = 80;
/** The base sinks below the horizon line (behind the far trees). */
const BASE_DROP = 2;

const STONE = 0xd2c9df;
const STONE_DARK = 0x8f86a8;
const ROOF = 0x4b3f7c;
const GOLD = 0xd9a845;

type Col = number;

/** Hazed colour: the tower sits far away, so it leans toward the sky's horizon colour. */
function haze(c: Col, sky: Col, k: number): Col {
  const a = new THREE.Color(c);
  a.lerp(new THREE.Color(sky), k);
  return a.getHex();
}

function towerGeometry(sky: Col): THREE.BufferGeometry {
  const h = (c: Col, k = 0.38): Col => haze(c, sky, k);
  const cyl = (rt: number, rb: number, ht: number, seg = 8): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, ht, seg).translate(0, ht / 2, 0);
  const cone = (r: number, ht: number, seg = 8): THREE.BufferGeometry => new THREE.ConeGeometry(r, ht, seg).translate(0, ht / 2, 0);
  const parts: THREE.BufferGeometry[] = [];
  // Stepped plinth.
  parts.push(paint(cyl(9.5, 11, 4), h(STONE_DARK), [0.6, 0.95]));
  parts.push(paint(xf(cyl(8.2, 9.2, 4), [0, 4, 0]), h(STONE), [0.7, 1]));
  // Main shaft, tapering, with buttress fins.
  parts.push(paint(xf(cyl(4.2, 6.8, 46), [0, 8, 0]), h(STONE), [0.62, 1.05]));
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    parts.push(paint(xf(new THREE.BoxGeometry(1.4, 30, 3.2).translate(0, 15, 0), [Math.cos(a) * 6.2, 8, Math.sin(a) * 6.2], [0, -a, 0.06]), h(STONE_DARK), [0.55, 0.95]));
  }
  // Balconies.
  for (const [y, r] of [
    [22, 7.6],
    [38, 6.6],
    [54, 5.6],
  ] as const)
    parts.push(paint(xf(cyl(r, r - 0.6, 1.3, 10), [0, y, 0]), h(GOLD, 0.3), [0.8, 1.1]));
  // Upper keep, crown of spikes, needle spire.
  parts.push(paint(xf(cyl(3.0, 4.2, 14), [0, 54, 0]), h(STONE), [0.75, 1.05]));
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    parts.push(paint(xf(cone(0.7, 5, 5), [Math.cos(a) * 3.4, 67.5, Math.sin(a) * 3.4], [Math.sin(a) * 0.25, 0, -Math.cos(a) * 0.25]), h(ROOF, 0.3), [0.8, 1.1]));
  }
  parts.push(paint(xf(cyl(3.4, 3.4, 1.2, 10), [0, 67.5, 0]), h(GOLD, 0.3), [0.8, 1.1]));
  parts.push(paint(xf(cone(2.8, 16), [0, 68.7, 0]), h(ROOF, 0.28), [0.7, 1.15]));
  return combine(parts);
}

/**
 * +valley: the Tower as a pale ruined spire (Sunmeadow's painted-valley look): white stone with
 * moss bands, open arches, a broken crown with floating shards. Same footprint and height.
 */
function ruinTowerGeometry(sky: Col): THREE.BufferGeometry {
  const h = (c: Col, k = 0.42): Col => haze(c, sky, k);
  const P = 0xf2eee4;
  const PD = 0xb8b8bc;
  const MOSS = 0x7fa850;
  const cyl = (rt: number, rb: number, ht: number, seg = 10): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, ht, seg).translate(0, ht / 2, 0);
  const parts: THREE.BufferGeometry[] = [];
  parts.push(paint(cyl(10, 11.5, 4), h(PD), [0.6, 0.95]));
  parts.push(paint(xf(cyl(8.4, 9.4, 3), [0, 4, 0]), h(P), [0.75, 1]));
  parts.push(paint(xf(cyl(9.0, 9.0, 0.8), [0, 6.6, 0]), h(MOSS, 0.35), [0.8, 1.05]));
  // Shaft with a broken, slanted top (the crown is gone).
  const shaft = new THREE.CylinderGeometry(4.0, 6.6, 52, 12, 6);
  const sp = shaft.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < sp.count; i++) {
    if (sp.getY(i) > 25.9) {
      const a = Math.atan2(sp.getZ(i), sp.getX(i));
      sp.setY(i, sp.getY(i) - (Math.sin(a * 2 + 0.6) * 0.5 + 0.5) * 9 - ((i * 7) % 5) * 0.6);
    }
  }
  shaft.computeVertexNormals();
  parts.push(paint(xf(shaft, [0, 7 + 26, 0]), h(P), [0.62, 1.08]));
  // Buttress fins, two of them snapped short.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const len = i % 2 ? 18 : 30;
    parts.push(paint(xf(new THREE.BoxGeometry(1.6, len, 3.2).translate(0, len / 2, 0), [Math.cos(a) * 6.2, 7, Math.sin(a) * 6.2], [0, -a, 0.05]), h(PD), [0.55, 0.98]));
  }
  // Moss bands and dark arched openings.
  for (const [y, r] of [
    [24, 6.0],
    [42, 4.9],
  ] as const)
    parts.push(paint(xf(cyl(r + 0.3, r + 0.5, 1.2, 12), [0, y, 0]), h(MOSS, 0.35), [0.8, 1.05]));
  for (let i = 0; i < 10; i++) {
    const y = 12 + i * 4.2;
    const a = i * 2.39;
    const r = 6.6 - (y - 7) * (2.6 / 52) + 0.15;
    parts.push(paint(xf(new THREE.BoxGeometry(1.4, 3.0, 0.3), [Math.cos(a) * r, y, Math.sin(a) * r], [0, Math.PI / 2 - a, 0]), h(0x3a4660, 0.45), [1, 1]));
  }
  // Floating shards of the lost crown.
  for (let i = 0; i < 4; i++) {
    const a = i * 1.7;
    parts.push(paint(xf(new THREE.OctahedronGeometry(1.6 + (i % 2), 0), [Math.cos(a) * 5.5, 66 + i * 4, Math.sin(a) * 5.5], [i, i * 0.7, 0], [0.8, 1.6, 0.8]), h(P, 0.35), [0.75, 1.05]));
  }
  return combine(parts);
}

export interface TowerLandmark {
  group: THREE.Group;
  /** Per frame: stay on the horizon in the tower's direction; turn the rings, pulse the beacon. */
  frame(camera: THREE.Camera, timeS: number): void;
  dispose(): void;
}

/**
 * The Tower in direction `dir` (sim space, from the map centre toward its `tower_landmark` hint).
 * `sky` is the theme's horizon colour (the haze).
 */
export function buildTowerLandmark(dir: { x: number; y: number }, sky: Col, style?: 'ruin', anchor?: { x: number; y: number; z: number; scale: number }): TowerLandmark {
  const len = Math.hypot(dir.x, dir.y) || 1;
  const dx = dir.x / len;
  const dz = -dir.y / len; // sim y → three -z
  const group = new THREE.Group();
  group.name = 'tower-landmark';
  const k = TOWER_HEIGHT / 85;
  group.scale.setScalar(k * (anchor?.scale ?? 1));
  // +atmos: a world anchor (three.js coords) stands the Tower among the distant ranges (real parallax).
  if (anchor) group.position.set(anchor.x, anchor.y, anchor.z);

  const ruin = style === 'ruin';
  const geo = ruin ? ruinTowerGeometry(sky) : towerGeometry(sky);
  const mat = new THREE.MeshBasicMaterial({ vertexColors: true, fog: false });
  const body = new THREE.Mesh(geo, mat);
  body.frustumCulled = false;
  group.add(body);

  // Window glints: small warm quads up the shaft (one merged additive mesh).
  const wins: THREE.BufferGeometry[] = [];
  for (let i = 0; i < (ruin ? 0 : 14); i++) {
    const y = 12 + i * 3.6;
    const a = i * 2.39;
    const r = 6.6 - (y - 8) * (2.6 / 46) + 0.12;
    wins.push(xf(new THREE.PlaneGeometry(0.9, 1.6), [Math.cos(a) * r, y, Math.sin(a) * r], [0, Math.PI / 2 - a, 0]));
  }
  const winGeo = wins.length ? combine(wins) : new THREE.BufferGeometry();
  const winMat = new THREE.MeshBasicMaterial({ color: 0xffd38a, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false });
  const windows = new THREE.Mesh(winGeo, winMat);
  windows.frustumCulled = false;
  if (wins.length) group.add(windows);

  // Two floating rings (the Tower's floors turning) and the beacon.
  const ringMat = new THREE.MeshBasicMaterial({ color: 0xffd98a, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
  const ringGeo = new THREE.TorusGeometry(1, 0.035, 6, 48);
  const rings = [
    { m: new THREE.Mesh(ringGeo, ringMat), y: 32, r: 12, tilt: 0.22, speed: 0.12 },
    { m: new THREE.Mesh(ringGeo, ringMat), y: 60, r: 8, tilt: -0.3, speed: -0.18 },
  ];
  if (ruin) rings.length = 0;
  for (const r of rings) {
    r.m.scale.setScalar(r.r);
    r.m.position.y = r.y;
    r.m.frustumCulled = false;
    group.add(r.m);
  }
  const beaconTex = radialTexture();
  const beaconMat = new THREE.SpriteMaterial({ map: beaconTex, color: ruin ? 0xbfe6ff : 0xfff0c8, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
  const beacon = new THREE.Sprite(beaconMat);
  beacon.position.y = ruin ? 76 : 86.5;
  beacon.scale.setScalar(16);
  beacon.frustumCulled = false;
  group.add(beacon);
  group.renderOrder = -5;

  // +beam: a tall soft pillar of light from the crown, seen across the map (strongest at dusk and night).
  const beam = buildBeam(ruin ? 76 : 86.5);
  group.add(beam.group);

  return {
    group,
    frame(camera, t) {
      const c = camera.position;
      if (!anchor) group.position.set(c.x + dx * TOWER_DISTANCE, -BASE_DROP, c.z + dz * TOWER_DISTANCE);
      for (const r of rings) {
        r.m.rotation.set(Math.PI / 2 + r.tilt, 0, t * r.speed);
      }
      const pulse = 0.5 + 0.5 * Math.sin(t * 1.6);
      beacon.scale.setScalar(14 + 5 * pulse);
      beaconMat.opacity = 0.65 + 0.35 * pulse;
    },
    dispose() {
      group.removeFromParent();
      geo.dispose();
      mat.dispose();
      winGeo.dispose();
      winMat.dispose();
      ringGeo.dispose();
      ringMat.dispose();
      beam.dispose();
      beaconTex?.dispose();
      beaconMat.dispose();
    },
  };
}

function radialTexture(): THREE.Texture | null {
  if (typeof document === 'undefined') return null;
  const S = 64;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d');
  if (!g) return null;
  const grd = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.25, 'rgba(255,236,190,0.7)');
  grd.addColorStop(1, 'rgba(255,220,150,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, S, S);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** +beam: beam height above the crown (Tower-local units; the group is scaled ~2.3×: ≈ 1.6 km). */
const BEAM_H = 700;

/**
 * +beam: the pillar of light: two open cylinders (a bright core, a wide soft glow) faded at the
 * edges by the view angle and up the height, gently pulsing, tinted by the time of day (ATMOS.uDay:
 * pale gold by day, cool and strong at dusk and night), plus motes drifting up it. Additive, no
 * depth writes, no fog: 3 draws, all motion in the shaders.
 */
function buildBeam(baseY: number): { group: THREE.Group; dispose(): void } {
  const group = new THREE.Group();
  group.name = 'tower-beam';
  const uniforms = { uTime: U.uTime, uDay: ATMOS.uDay };
  const beamMat = (strength: number, width: number): THREE.ShaderMaterial =>
    new THREE.ShaderMaterial({
      uniforms: { ...uniforms, uStrength: { value: strength }, uWidth: { value: width } },
      vertexShader: `varying vec3 vN; varying vec3 vW; varying float vT;
        void main(){ vT = uv.y; vN = normalize(mat3(modelMatrix) * normal); vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
      fragmentShader: `uniform float uTime; uniform float uDay; uniform float uStrength; uniform float uWidth;
        varying vec3 vN; varying vec3 vW; varying float vT;
        void main(){
          vec3 v = normalize(cameraPosition - vW);
          vec3 n = normalize(vec3(vN.x, 0.0, vN.z));
          vec3 vh = normalize(vec3(v.x, 0.0, v.z));
          float edge = pow(clamp(abs(dot(n, vh)), 0.0, 1.0), uWidth);
          float rise = smoothstep(0.0, 0.03, vT) * (1.0 - smoothstep(0.35, 1.0, vT));
          float pulse = 0.86 + 0.14 * sin(uTime * 1.25 - vT * 9.0) + 0.05 * sin(uTime * 3.1 + vT * 23.0);
          float night = 1.0 - clamp(uDay, 0.0, 1.0);
          float k = uStrength * edge * rise * pulse * mix(0.62, 1.15, night);
          vec3 col = mix(vec3(1.0, 0.88, 0.62), vec3(0.62, 0.82, 1.0), night * 0.8);
          gl_FragColor = vec4(col * k, 1.0);
        }`,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: false,
    });
  const core = new THREE.CylinderGeometry(3.4, 4.6, BEAM_H, 16, 1, true).translate(0, BEAM_H / 2, 0);
  const glow = new THREE.CylinderGeometry(12, 15, BEAM_H, 16, 1, true).translate(0, BEAM_H / 2, 0);
  const coreMat = beamMat(1.25, 1.4);
  const glowMat = beamMat(0.45, 2.6);
  const coreMesh = new THREE.Mesh(core, coreMat);
  const glowMesh = new THREE.Mesh(glow, glowMat);
  for (const m of [coreMesh, glowMesh]) {
    m.position.y = baseY - 4;
    m.frustumCulled = false;
    m.renderOrder = 2;
    group.add(m);
  }
  // Motes: points rising up the beam (positions animated in the vertex shader).
  const N = 90;
  const pos = new Float32Array(N * 3);
  const seed = new Float32Array(N);
  let r = 12345;
  const rnd = (): number => {
    r = (r * 16807) % 2147483647;
    return r / 2147483647;
  };
  for (let i = 0; i < N; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 9;
    pos[i * 3] = Math.cos(a) * d;
    pos[i * 3 + 1] = rnd();
    pos[i * 3 + 2] = Math.sin(a) * d;
    seed[i] = rnd();
  }
  const mg = new THREE.BufferGeometry();
  mg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  mg.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
  const moteMat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: `uniform float uTime; attribute float aSeed; varying float vA;
      void main(){
        float t = fract(position.y + uTime * (0.012 + 0.018 * aSeed));
        vec3 p = vec3(position.x + sin(uTime * 0.7 + aSeed * 20.0) * 1.5, ${baseY.toFixed(1)} + t * ${(BEAM_H * 0.45).toFixed(1)}, position.z + cos(uTime * 0.6 + aSeed * 17.0) * 1.5);
        vA = smoothstep(0.0, 0.08, t) * (1.0 - smoothstep(0.6, 1.0, t)) * (0.6 + 0.4 * sin(uTime * 2.0 + aSeed * 40.0));
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = clamp(2600.0 / -mv.z, 1.5, 7.0);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `uniform float uDay; varying float vA;
      void main(){
        vec2 c = gl_PointCoord - 0.5; float d = dot(c, c);
        if (d > 0.25) discard;
        float night = 1.0 - clamp(uDay, 0.0, 1.0);
        float a = vA * (1.0 - d * 4.0) * mix(0.45, 1.0, night);
        gl_FragColor = vec4(mix(vec3(1.0, 0.9, 0.65), vec3(0.75, 0.9, 1.0), night) * a, 1.0);
      }`,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: false,
  });
  const motes = new THREE.Points(mg, moteMat);
  motes.frustumCulled = false;
  motes.renderOrder = 3;
  motes.name = 'tower-beam-motes';
  group.add(motes);
  return {
    group,
    dispose() {
      core.dispose();
      glow.dispose();
      coreMat.dispose();
      glowMat.dispose();
      mg.dispose();
      moteMat.dispose();
    },
  };
}
